import React, { useEffect, useState, useRef } from 'react';

import CallTile from './CallTile';
import { useCall } from '../../context/CallContext';

/**
 * Keep the call initiator in a separate bottom-left dock. Other participants
 * share the remaining space; on small call windows only their grid scrolls.
 * Container queries respond to resizing the call window, not just the browser.
 *
 * While somebody is sharing a screen the stage rearranges: the screen takes
 * almost all of it and every face, the host included, stacks into a rail down
 * the right-hand side. The grid is not merely shrunk, because a shared screen is
 * the thing being talked about and a row of thumbnails competing with it for
 * space helps nobody read the slide.
 */

/** One <video>, fed from a MediaStream rather than a src. */
const ScreenView = ({ stream }) => {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        el.srcObject = stream || null;
        if (stream) el.play().catch(() => { /* muted, so this is rare */ });
        return () => { el.srcObject = null; };
    }, [stream]);

    /*
     * `muted` because a shared screen carries no audio in this version, and
     * because if it ever does, the audio belongs to the sinks in the provider —
     * they survive the window being minimised and a <video> here would not.
     *
     * `object-fit: contain` rather than cover: cropping somebody's screen to
     * fill a box is how the line of code being pointed at ends up off-frame.
     */
    return <video ref={ref} autoPlay playsInline muted className="call-spotlight-video" />;
};

// How often the active-speaker ring is recomputed. Fast enough to follow a
// conversation, slow enough that five tiles are not re-rendering at 60Hz to
// move a border.
const LEVEL_POLL_MS = 400;

// On above, off below, so the ring does not strobe on the gaps between words.
const SPEAKING_ON = 0.045;
const SPEAKING_OFF = 0.02;

const CallStage = () => {
    const { call, remoteMedia, localStream, screenStream, levels, me } = useCall();
    const [speaking, setSpeaking] = useState({});
    const speakingRef = useRef({});

    const joined = call.participants.filter(
        (p) => p.state === 'joined' || p.state === 'ringing'
    );

    /*
     * Who is talking, read off each receiver rather than computed from the
     * audio.
     *
     * getSynchronizationSources gives a level the browser has already worked out
     * for every incoming stream. The obvious alternative — an AnalyserNode per
     * participant — means five Web Audio graphs running continuously to decide
     * where to draw a border, and a second path playing the same audio if any
     * of them is ever connected to a destination by mistake.
     */
    useEffect(() => {
        if (call.status !== 'active') return undefined;

        const timer = setInterval(() => {
            const current = levels();
            let changed = false;
            const next = { ...speakingRef.current };

            for (const participant of call.participants) {
                if (participant.userId === me) continue;
                const level = current[participant.userId];
                if (level == null) continue;
                const was = Boolean(next[participant.userId]);
                const now = level > (was ? SPEAKING_OFF : SPEAKING_ON);
                if (now !== was) {
                    next[participant.userId] = now;
                    changed = true;
                }
            }

            if (changed) {
                speakingRef.current = next;
                setSpeaking(next);
            }
        }, LEVEL_POLL_MS);

        return () => clearInterval(timer);
    }, [call.status, call.participants, levels, me]);

    const host = joined.find(participant => participant.isInitiator);
    const guests = joined.filter(participant => participant !== host);
    const columns = Math.max(1, Math.min(3, Math.ceil(Math.sqrt(guests.length || 1))));
    const tile = (participant) => (
        <CallTile
            key={participant.userId}
            participant={participant}
            stream={participant.userId === me ? localStream : remoteMedia[participant.userId]?.camera}
            speaking={participant.userId !== me && Boolean(speaking[participant.userId])}
            mirror={participant.userId === me}
        />
    );

    /*
     * Whoever holds the screen. The server allows exactly one at a time, so this
     * is a find rather than a list — and our own share is authoritative locally,
     * because the capture starts before the server has confirmed it.
     */
    const sharer = screenStream
        ? joined.find(participant => participant.userId === me)
        : joined.find(participant => participant.sharingScreen);
    const shared = sharer && (sharer.userId === me
        ? screenStream
        : remoteMedia[sharer.userId]?.screen);

    if (sharer && shared) {
        const name = sharer.userId === me ? 'Your' : `${sharer.person?.name || 'Someone'}'s`;
        return (
            <div className="call-stage is-sharing">
                <div className="call-spotlight">
                    <ScreenView stream={shared} />
                    <span className="call-spotlight-label">{name} screen</span>
                </div>
                <div className="call-rail" aria-label="People on the call">
                    {joined.map(tile)}
                </div>
            </div>
        );
    }

    return (
        <div className="call-stage">
            <div className="call-participant-grid" style={{ '--call-columns': columns }}>
                {guests.map(tile)}
                {!guests.length && <div className="call-stage-empty">Waiting for people to join</div>}
            </div>
            {host && <div className="call-host-dock" aria-label="Call host">{tile(host)}</div>}
        </div>
    );
};

export default CallStage;
